"use client";

import { c as _c } from "react-compiler-runtime";
import { Menu } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import AgelessLogo from "@/assets/brand/ageless-logo.svg";
import { AGELESS_ADMIN_URL, AGELESS_BOOK_DEMO_URL } from "@/components/layout/marketing.constants";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { type BCP47LangCode } from "@/core/i18n";
import { useTranslation } from "@/core/i18n";
type MarketingHeaderProps = {
  readonly lang: BCP47LangCode;
};
const MarketingHeader = t0 => {
  const $ = _c(76);
  const {
    lang
  } = t0;
  const {
    t
  } = useTranslation();
  const pathname = usePathname();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const homePath = `/${lang}`;
  const isHome = pathname === homePath || pathname === `${homePath}/`;
  let t1;
  if ($[0] !== t) {
    t1 = t("marketingHeader.navHowItWorks");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `${homePath}#how-it-works`;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      label: t1,
      href: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("marketingHeader.navFeatures");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = `${homePath}#features`;
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = {
      label: t4,
      href: t5
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = [t3, t6];
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const navLinks = t7;
  let t8;
  if ($[13] !== t) {
    t8 = t("marketingHeader.drawer.home");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== homePath || $[16] !== t8) {
    t9 = {
      label: t8,
      href: homePath
    };
    $[15] = homePath;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t) {
    t10 = t("marketingHeader.drawer.signUp");
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = `${homePath}/signup/start`;
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = {
      label: t10,
      href: t11
    };
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t9) {
    t13 = [t9, t12];
    $[23] = t12;
    $[24] = t9;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const drawerLinks = t13;
  let t14;
  if ($[26] !== t) {
    t14 = t("marketingHeader.ariaHome");
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <AgelessLogo aria-hidden="true" className="text-foreground h-14 w-32 md:h-20" fill="currentColor" stroke="none" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== homePath || $[30] !== t14) {
    t16 = <Link aria-label={t14} className="inline-flex items-center" href={homePath}>{t15}</Link>;
    $[29] = homePath;
    $[30] = t14;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== isHome || $[33] !== navLinks || $[34] !== t) {
    t17 = isHome ? <nav aria-label={t("marketingHeader.navAriaLabel")} className="absolute top-1/2 left-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-1 md:flex">{navLinks.map(_temp)}</nav> : null;
    $[32] = isHome;
    $[33] = navLinks;
    $[34] = t;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t) {
    t18 = t("marketingHeader.logIn");
    $[36] = t;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18) {
    t19 = <a className="type-overline text-muted-foreground hover:text-foreground hidden transition-colors duration-300 sm:inline-flex" href={AGELESS_ADMIN_URL} rel="noopener noreferrer" target="_blank">{t18}</a>;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t) {
    t20 = t("marketingHeader.bookDemo");
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t20) {
    t21 = <a className="prelaunch-cta prelaunch-cta--compact inline-flex items-center justify-center" href={AGELESS_BOOK_DEMO_URL} rel="noopener noreferrer" target="_blank"><span className="prelaunch-cta-label type-overline whitespace-nowrap">{t20}</span></a>;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t) {
    t22 = t("marketingHeader.openMenu");
    $[44] = t;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => setDrawerOpen(true);
    t24 = <Menu className="h-5 w-5" />;
    $[46] = t23;
    $[47] = t24;
  } else {
    t23 = $[46];
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t22) {
    t25 = <Button aria-label={t22} className="h-10 w-10" size="icon" variant="ghost" onClick={t23}>{t24}</Button>;
    $[48] = t22;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t) {
    t26 = t("marketingHeader.openMenu");
    $[50] = t;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== t26) {
    t27 = <SheetTitle className="sr-only">{t26}</SheetTitle>;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t) {
    t28 = t("marketingHeader.navAriaLabel");
    $[54] = t;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== drawerLinks) {
    t29 = <ul className="space-y-6">{drawerLinks.map(link_0 => <li key={link_0.href}><Link className="type-heading-md text-foreground hover:text-foreground/70 block font-light transition-colors" href={link_0.href} onClick={() => setDrawerOpen(false)}>{link_0.label}</Link></li>)}</ul>;
    $[56] = drawerLinks;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t28 || $[59] !== t29) {
    t30 = <div className="flex h-full flex-col"><nav aria-label={t28} className="flex-1 px-8 pt-20 pb-10">{t29}</nav></div>;
    $[58] = t28;
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== t27 || $[62] !== t30) {
    t31 = <SheetContent className="rounded-none p-0" side="right">{t27}{t30}</SheetContent>;
    $[61] = t27;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== drawerOpen || $[65] !== t25 || $[66] !== t31) {
    t32 = <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>{t25}{t31}</Sheet>;
    $[64] = drawerOpen;
    $[65] = t25;
    $[66] = t31;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== t19 || $[69] !== t21 || $[70] !== t32) {
    t33 = <div className="flex items-center gap-2 md:gap-3" data-prelaunch={true}>{t19}{t21}{t32}</div>;
    $[68] = t19;
    $[69] = t21;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t16 || $[73] !== t17 || $[74] !== t33) {
    t34 = <header className="border-mocha/20 fixed top-0 right-0 left-0 z-50 border-b bg-white backdrop-blur-md" data-marketing-header=""><div className="marketing-container"><div className="relative flex h-14 items-center justify-between md:h-20">{t16}{t17}{t33}</div></div></header>;
    $[72] = t16;
    $[73] = t17;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  return t34;
};
export default MarketingHeader;
function _temp(link, index) {
  return <span className="flex items-center gap-1" key={link.href}>{index > 0 ? <span aria-hidden="true" className="text-foreground/30">|</span> : null}<a className="type-body-sm text-muted-foreground hover:text-foreground px-3 py-1.5 whitespace-nowrap transition-colors duration-300" href={link.href} style={{
      fontWeight: 400,
      letterSpacing: "0.51px"
    }}>{link.label}</a></span>;
}